import { useState, useEffect, useRef } from "react";
import { Box, Button, Card, CardMedia, Stack, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const useStyles = (theme, maxHeight, maxWidth) => ({
  root: {
    borderRadius: '8px',
    overflow: 'auto',
    backgroundColor: theme.palette.grey[100],
    width: 'fit-content',
    paddingBottom: theme.spacing(1.25)
  },
  scrollContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: theme.spacing(0.25),
    maxHeight: maxHeight,
    maxWidth: maxWidth,
    marginBottom: theme.spacing(1)
  },
  card: (isSelected) => ({
    border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
    cursor: 'pointer',
    margin: theme.spacing(0.5),
    width: '140px',
    boxShadow: isSelected ? '0 4px 10px rgba(0, 0, 0, 0.5)' : 'none',
  }),
  cardMedia: {
    height: '150px',
    objectFit: 'cover'
  }
});

export default function GooglePhotos({ accessToken, setSelectedImages, maxHeight = '500px', maxWidth = '1000px' }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const classes = useStyles(theme, maxHeight, maxWidth); // Use styles

  const fetchPhotos = async (pageToken = "") => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://photoslibrary.googleapis.com/v1/mediaItems?pageSize=50&pageToken=${pageToken}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json();
      // Filter out videos
      setPhotos((prevPhotos) => {
        const existingIds = new Set(prevPhotos.map(photo => photo.id)); // Create a set of existing photo IDs
        const newPhotos = data.mediaItems.filter(item => 
          !item.mimeType.startsWith('video') && !existingIds.has(item.id) // Filter out videos and duplicates
        );
        return [...prevPhotos, ...newPhotos]; // Combine previous and new photos
      });
      setNextPageToken(data.nextPageToken);
    } catch (error) {
      console.error("Error fetching photos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    // Fetch photos initially
    fetchPhotos();
  }, [accessToken]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Calculate scroll position and container dimensions
    const scrollPosition = container.scrollTop;
    const visibleHeight = container.clientHeight;
    const totalHeight = container.scrollHeight;
    
    // Load more when user scrolls to within 100px of the bottom
    const scrollThreshold = 300;
    const isNearBottom = totalHeight - (scrollPosition + visibleHeight) <= scrollThreshold;

    if (isNearBottom && nextPageToken && !isLoading) {
      // Only fetch photos if there is a nextPageToken
      if (nextPageToken) {
        fetchPhotos(nextPageToken);
      }
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const throttledScrollHandler = () => {
      // Throttle scroll events to prevent too many calls
      if (!throttledScrollHandler.timeout) {
        throttledScrollHandler.timeout = setTimeout(() => {
          handleScroll();
          throttledScrollHandler.timeout = null;
        }, 200);
      }
    };

    container.addEventListener("scroll", throttledScrollHandler);
    return () => {
      container.removeEventListener("scroll", throttledScrollHandler);
      if (throttledScrollHandler.timeout) {
        clearTimeout(throttledScrollHandler.timeout);
      }
    };
  }, [nextPageToken, isLoading]);

  const handleSelectPhoto = (photo) => {
    setSelectedPhotos((prevSelected) => {
      const isSelected = prevSelected.some((p) => p.id === photo.id);
      if (isSelected) {
        // Remove the photo if it's already selected
        return prevSelected.filter((p) => p.id !== photo.id);
      } else {
        // Add the photo object with id, url, and additional information
        return [...prevSelected, { id: photo.id, url: photo.baseUrl, filename: photo.filename }];
      }
    });
  };

  const handleSaveSelected = async () => {
    try {
      const newImages = selectedPhotos.map(photo => ({
        file: null,
        preview: photo.url,
        isExisting: false, 
        url: null 
      }));

      setSelectedImages(prev => [...prev, ...newImages]);
      setSelectedPhotos([])
    } catch (error) {
      console.error("Error processing photos:", error);
    }
  };

  return (
    <Box sx={classes.root}>
      <Box 
        ref={scrollContainerRef}
        sx={classes.scrollContainer}
      >
        {photos.map((photo) => (
          <Card 
            key={photo.id} 
            onClick={() => handleSelectPhoto(photo)} 
            sx={classes.card(selectedPhotos.some((p) => p.id === photo.id))}
          >
            <CardMedia
              component="img"
              image={photo.baseUrl}
              alt={photo.filename}
              sx={classes.cardMedia}
            />
          </Card>
        ))}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', padding: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
      <Stack direction="row" justifyContent="center" sx={{ marginTop: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleSaveSelected()} 
        >
          Upload Selected To Bondly
        </Button>
      </Stack>
    </Box>
  );
}
